'use client';

import { useState, useRef } from 'react';
import FadeIn from '@/components/ui/FadeIn';
import SectionHeader from '@/components/ui/SectionHeader';
import ReviewsControls from './reviews/ReviewsControls';
import CarouselDots from './reviews/CarouselDots';
import {
  ReviewsCarousel,
  StaticReviewsSlide,
  MobileReviewsSlider,
} from './reviews/ReviewsCarousel';
import type { MobileSliderHandle } from './reviews/ReviewsCarousel';
import type { CustomerReviewsBlockData } from '@/lib/contentful/types';

export default function CustomerReviewsBlock({
  data,
  googleReviewsUrl,
}: {
  data: CustomerReviewsBlockData;
  googleReviewsUrl?: string;
}) {
  const [current, setCurrent] = useState(0);
  const sliderRef = useRef<MobileSliderHandle>(null);

  if (data.reviews.length === 0) return null;

  const PER_SLIDE = 5; // 1 featured + up to 4 smalls
  const slideCount = Math.max(1, Math.ceil(data.reviews.length / PER_SLIDE));
  const byLength = [...data.reviews].sort(
    (a, b) => b.text.length - a.text.length,
  );
  const featured = byLength.slice(0, slideCount);
  const smalls = byLength.slice(slideCount);
  const groups = featured.map((feat, i) => [
    feat,
    ...smalls.filter((_, j) => j % slideCount === i),
  ]);
  const hasCarousel = groups.length > 1;
  const total = groups.length;

  const prev = () => {
    sliderRef.current?.goPrev();
    setCurrent((c) => (c - 1 + total) % total);
  };
  const next = () => {
    sliderRef.current?.goNext();
    setCurrent((c) => (c + 1) % total);
  };

  return (
    <section
      id={data.id}
      className={`py-24 px-[6vw] scroll-mt-[var(--header-height)]${data.bgColor ? ` bg-${data.bgColor}` : ''}`}
      aria-labelledby="reviews-title"
    >
      <div className="max-w-[1024px] mx-auto">
        <FadeIn className="flex flex-col tablet:flex-row items-start tablet:items-end justify-between gap-6 mb-6 tablet:mb-12 flex-wrap">
          <SectionHeader
            eyebrow={data.label}
            title={data.title}
            titleId="reviews-title"
            className="mb-0"
          />
          <ReviewsControls
            showRating={data.showAverageRating}
            averageRating={data.averageRating}
            showReviewsLink={data.showReviewsLink}
            googleReviewsUrl={googleReviewsUrl}
            hasCarousel={hasCarousel}
            onPrev={prev}
            onNext={next}
          />
        </FadeIn>

        {/* Mobile: slider por card, sem overflow */}
        <div className="tablet:hidden">
          <MobileReviewsSlider ref={sliderRef} reviews={byLength} />
        </div>

        {/* Desktop: carousel com slides */}
        <div className="hidden tablet:block">
          {hasCarousel ? (
            <>
              <ReviewsCarousel groups={groups} current={current} />
              <CarouselDots
                total={total}
                current={current}
                onSelect={setCurrent}
              />
            </>
          ) : (
            <StaticReviewsSlide group={groups[0]} />
          )}
        </div>
      </div>
    </section>
  );
}
